import { useState } from 'react'
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Heading,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { LuTrash } from 'react-icons/lu'
import EventQR from '../EventQr'
import { EventUsers } from '../EventUsers'
import { EventEmailTemplates } from '../EventEmailTemplates'
import ChangeEventTypeDialog from '../ChangeEventTypeDialog'
import { toaster } from '../ui/toaster'
import { deleteEvent } from '../../services/firebaseService'
import { normalizeTier } from '../../constants/analyticsTiers'
import { useEventTypes } from '../../contexts/EventTypesContext'
import { useTranslation } from '../../i18n'

// ---- Team ------------------------------------------------------------------

// Roles are added and removed on the spot, so this tab has no Edit button.
export function AdminsSection({ s }) {
  const { lang } = useTranslation()
  return (
    <EventUsers
      eventId={s.eventId}
      adminRole={s.typeAdminRole}
      typeLabel={s.typeConfig?.label?.[lang] || s.typeConfig?.label?.en}
    />
  )
}

// Each template has its own editor and Save.
export function EmailSection({ s }) {
  return <EventEmailTemplates eventId={s.eventId} emailLanguage={s.event?.emailLanguage} />
}

// ---- Event -----------------------------------------------------------------

export function ShareSection({ s }) {
  return <EventQR eventId={s.eventId} webappUrl={s.webappUrl} hideTitle />
}

export function PlanSection({ s }) {
  const { t } = useTranslation()
  return (
    <Field.Root>
      <Field.Label>{t('analytics.tier')}</Field.Label>
      <NativeSelect.Root w={{ base: '100%', sm: '200px' }} disabled={!s.editing}>
        <NativeSelect.Field
          value={normalizeTier(s.draft?.tier)}
          onChange={(e) => s.setField('tier', e.target.value)}
        >
          <option value="free">{t('analytics.tierFree')}</option>
          <option value="pro">{t('analytics.tierPro')}</option>
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </Field.Root>
  )
}

// The name is a draft field like any other. Changing the type and deleting the
// event are not: each is its own confirmed action, as it was in the old header.
export function GeneralSection({ s }) {
  const { t, lang } = useTranslation()
  const navigate = useNavigate()
  const { typesList } = useEventTypes()
  const [pendingType, setPendingType] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deleteEvent(s.eventId)
      toaster.create({ title: t('events.eventDeleted'), status: 'success' })
      // replace: Back must not land on the page of an event that no longer exists
      navigate('/events', { replace: true })
    } catch (err) {
      toaster.create({ title: t('events.eventDeleteFailed'), description: err.message, status: 'error' })
      setDeleting(false)
      setDeleteOpen(false)
    }
  }

  return (
    <Stack gap={8}>
      <Field.Root maxW="480px">
        <Field.Label>{t('events.eventName')}</Field.Label>
        <Input
          value={s.draft?.name ?? ''}
          onChange={(e) => s.setField('name', e.target.value)}
          disabled={!s.editing}
          placeholder={t('events.eventNameEditPlaceholder')}
        />
      </Field.Root>

      {s.isSysAdmin && s.eventId && (
        <Field.Root maxW="480px">
          <Field.Label>{t('eventTypes.changeTitle')}</Field.Label>
          <NativeSelect.Root w={{ base: '100%', sm: '240px' }}>
            <NativeSelect.Field
              value={s.eventTypeId}
              onChange={(e) => { if (e.target.value !== s.eventTypeId) setPendingType(e.target.value) }}
            >
              {typesList.map((tc) => (
                <option key={tc.id} value={tc.id}>{tc.label?.[lang] || tc.label?.en || tc.id}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <Field.HelperText>{t('workspace.changeTypeHelp')}</Field.HelperText>
        </Field.Root>
      )}

      {s.isSysAdmin && s.eventId && (
        <Box borderTop="1px solid" borderColor="border" pt={6}>
          <Heading size="sm" mb={1}>{t('events.deleteEventTitle')}</Heading>
          <Text fontSize="sm" color="fg.muted" mb={3} maxW="65ch">{t('workspace.deleteHelp')}</Text>
          <Button colorPalette="red" variant="outline" size="sm" onClick={() => setDeleteOpen(true)}>
            <LuTrash /> {t('events.deleteEventAria')}
          </Button>
        </Box>
      )}

      <ChangeEventTypeDialog
        open={!!pendingType}
        onClose={() => setPendingType(null)}
        eventId={s.eventId}
        eventData={s.event}
        currentType={s.eventTypeId}
        nextType={pendingType || s.eventTypeId}
        onChanged={() => setPendingType(null)}
      />

      <Dialog.Root open={deleteOpen} onOpenChange={(e) => { if (!deleting) setDeleteOpen(e.open) }} role="alertdialog">
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>{t('events.deleteEventTitle')}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Text>{t('events.deleteEventConfirm', { name: s.event?.name || s.eventId })}</Text>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="outline" disabled={deleting} onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
                <Button colorPalette="red" loading={deleting} onClick={handleDelete}>{t('common.delete')}</Button>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" disabled={deleting} />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Stack>
  )
}
